import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';
import Navbar from '../../components/Navbar';

function Performance() {
  const [restaurants, setRestaurants] = useState([]);
  const [deliveryPartners, setDeliveryPartners] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPerformance();
  }, []);

  const fetchPerformance = async () => {
    try {
      const [restResponse, deliveryResponse] = await Promise.all([
        adminAPI.getRestaurantPerformance(),
        adminAPI.getDeliveryPerformance()
      ]);

      setRestaurants(restResponse.data.data.restaurants);
      setDeliveryPartners(deliveryResponse.data.data.deliveryPartners);
    } catch (error) {
      console.error('Failed to fetch performance data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRatingColor = (rating) => {
    if (rating >= 4.5) return 'linear-gradient(135deg, #51cf66 0%, #40c057 100%)';
    if (rating >= 4.0) return 'linear-gradient(135deg, #4ecdc4 0%, #44b8b1 100%)';
    if (rating >= 3.0) return 'linear-gradient(135deg, #ffa500 0%, #ff8c00 100%)';
    return 'linear-gradient(135deg, #ff6b6b 0%, #e85a5a 100%)';
  };

  if (loading) return <div className="loading">🔄 Loading performance metrics...</div>;

  return (
    <div style={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #e8eef3 100%)', minHeight: '100vh' }}>
      <Navbar />
      <div className="container">
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 'var(--space-2xl)' }} className="fade-in">
          <h1 style={{
            fontSize: 'var(--font-size-4xl)',
            fontWeight: '800',
            background: 'var(--gradient-primary)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            marginBottom: 'var(--space-sm)'
          }}>
            📊 Performance Metrics
          </h1>
          <p style={{ color: 'var(--gray-600)', fontSize: 'var(--font-size-lg)' }}>
            Monitor performance across restaurants and delivery partners
          </p>
        </div>

        {/* Restaurant Performance Section */}
        <div className="card fade-in" style={{ marginBottom: 'var(--space-2xl)' }}>
          <h2 style={{
            fontSize: 'var(--font-size-3xl)',
            fontWeight: '700',
            marginBottom: 'var(--space-lg)',
            color: 'var(--gray-900)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-sm)'
          }}>
            <span>🏪</span>
            <span>Restaurant Performance</span>
          </h2>

          {restaurants.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 'var(--space-2xl)' }}>
              <div style={{ fontSize: '80px', marginBottom: 'var(--space-md)' }}>🏪</div>
              <p style={{ color: 'var(--gray-600)', fontSize: 'var(--font-size-lg)' }}>
                No restaurants registered yet
              </p>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 'var(--space-lg)' }}>
              {restaurants.map(restaurant => (
                <div key={restaurant._id} style={{
                  padding: 'var(--space-lg)',
                  background: 'var(--gray-50)',
                  borderRadius: 'var(--radius-lg)',
                  border: '1px solid var(--gray-200)',
                  display: 'grid',
                  gridTemplateColumns: '1fr auto auto',
                  gap: 'var(--space-lg)',
                  alignItems: 'center',
                  transition: 'var(--transition-base)'
                }}
                className="hover-lift"
                >
                  {/* Restaurant Name */}
                  <div>
                    <h3 style={{
                      fontSize: 'var(--font-size-xl)',
                      fontWeight: '700',
                      color: 'var(--gray-900)',
                      marginBottom: 'var(--space-xs)'
                    }}>
                      {restaurant.name}
                    </h3>
                    <p style={{
                      fontSize: 'var(--font-size-sm)',
                      color: 'var(--gray-600)',
                      margin: 0
                    }}>
                      Restaurant Performance Metrics
                    </p>
                  </div>

                  {/* Rating Badge */}
                  <div style={{
                    padding: 'var(--space-md) var(--space-lg)',
                    borderRadius: 'var(--radius-md)',
                    background: getRatingColor(restaurant.rating || 0),
                    color: 'white',
                    textAlign: 'center',
                    minWidth: '120px'
                  }}>
                    <p style={{
                      fontSize: 'var(--font-size-xs)',
                      fontWeight: '600',
                      marginBottom: 'var(--space-xs)',
                      opacity: 0.9
                    }}>
                      RATING
                    </p>
                    <p style={{
                      fontSize: 'var(--font-size-2xl)',
                      fontWeight: '800',
                      margin: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 'var(--space-xs)'
                    }}>
                      <span>{restaurant.rating || 0}</span>
                      <span>⭐</span>
                    </p>
                  </div>

                  {/* Orders Badge */}
                  <div style={{
                    padding: 'var(--space-md) var(--space-lg)',
                    borderRadius: 'var(--radius-md)',
                    background: 'var(--gradient-secondary)',
                    color: 'white',
                    textAlign: 'center',
                    minWidth: '120px'
                  }}>
                    <p style={{
                      fontSize: 'var(--font-size-xs)',
                      fontWeight: '600',
                      marginBottom: 'var(--space-xs)',
                      opacity: 0.9
                    }}>
                      TOTAL ORDERS
                    </p>
                    <p style={{
                      fontSize: 'var(--font-size-2xl)',
                      fontWeight: '800',
                      margin: 0
                    }}>
                      {restaurant.totalOrders || 0}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Delivery Partner Performance Section */}
        <div className="card fade-in">
          <h2 style={{
            fontSize: 'var(--font-size-3xl)',
            fontWeight: '700',
            marginBottom: 'var(--space-lg)',
            color: 'var(--gray-900)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-sm)'
          }}>
            <span>🚴</span>
            <span>Delivery Partner Performance</span>
          </h2>

          {deliveryPartners.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 'var(--space-2xl)' }}>
              <div style={{ fontSize: '80px', marginBottom: 'var(--space-md)' }}>🚴</div>
              <p style={{ color: 'var(--gray-600)', fontSize: 'var(--font-size-lg)' }}>
                No delivery partners registered yet
              </p>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 'var(--space-lg)' }}>
              {deliveryPartners.map(partner => (
                <div key={partner._id} style={{
                  padding: 'var(--space-lg)',
                  background: 'var(--gray-50)',
                  borderRadius: 'var(--radius-lg)',
                  border: '1px solid var(--gray-200)',
                  transition: 'var(--transition-base)'
                }}
                className="hover-lift"
                >
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr auto',
                    gap: 'var(--space-lg)',
                    alignItems: 'center',
                    marginBottom: 'var(--space-md)'
                  }}>
                    {/* Partner Name */}
                    <div>
                      <h3 style={{
                        fontSize: 'var(--font-size-xl)',
                        fontWeight: '700',
                        color: 'var(--gray-900)',
                        marginBottom: 'var(--space-xs)'
                      }}>
                        {partner.name}
                      </h3>
                      <p style={{
                        fontSize: 'var(--font-size-sm)',
                        color: 'var(--gray-600)',
                        margin: 0
                      }}>
                        Delivery Partner Performance
                      </p>
                    </div>

                    {/* Status Badge */}
                    {partner.deliveryPartner?.isApproved ? (
                      <div style={{
                        padding: 'var(--space-sm) var(--space-lg)',
                        borderRadius: 'var(--radius-full)',
                        background: 'var(--gradient-success)',
                        color: 'white',
                        fontWeight: '700',
                        fontSize: 'var(--font-size-sm)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-xs)'
                      }}>
                        <span>✅</span>
                        <span>Approved</span>
                      </div>
                    ) : (
                      <div style={{
                        padding: 'var(--space-sm) var(--space-lg)',
                        borderRadius: 'var(--radius-full)',
                        background: 'linear-gradient(135deg, #ffa500 0%, #ff8c00 100%)',
                        color: 'white',
                        fontWeight: '700',
                        fontSize: 'var(--font-size-sm)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-xs)'
                      }}>
                        <span>⏳</span>
                        <span>Pending</span>
                      </div>
                    )}
                  </div>

                  {/* Performance Metrics */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                    gap: 'var(--space-md)'
                  }}>
                    {/* Rating */}
                    <div style={{
                      padding: 'var(--space-md)',
                      borderRadius: 'var(--radius-md)',
                      background: getRatingColor(partner.deliveryPartner?.rating || 0),
                      color: 'white',
                      textAlign: 'center'
                    }}>
                      <p style={{
                        fontSize: 'var(--font-size-xs)',
                        fontWeight: '600',
                        marginBottom: 'var(--space-xs)',
                        opacity: 0.9
                      }}>
                        RATING
                      </p>
                      <p style={{
                        fontSize: 'var(--font-size-xl)',
                        fontWeight: '800',
                        margin: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 'var(--space-xs)'
                      }}>
                        <span>{partner.deliveryPartner?.rating || 0}</span>
                        <span>⭐</span>
                      </p>
                    </div>

                    {/* Total Deliveries */}
                    <div style={{
                      padding: 'var(--space-md)',
                      borderRadius: 'var(--radius-md)',
                      background: 'var(--gradient-primary)',
                      color: 'white',
                      textAlign: 'center'
                    }}>
                      <p style={{
                        fontSize: 'var(--font-size-xs)',
                        fontWeight: '600',
                        marginBottom: 'var(--space-xs)',
                        opacity: 0.9
                      }}>
                        DELIVERIES
                      </p>
                      <p style={{
                        fontSize: 'var(--font-size-xl)',
                        fontWeight: '800',
                        margin: 0
                      }}>
                        {partner.deliveryPartner?.totalDeliveries || 0}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Performance;
